import React, { useEffect, useState } from 'react';
import { Container, Heading, Box, Text, SimpleGrid, Image, Spinner, Center, Tag, Button, ButtonGroup, Wrap, WrapItem } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getDisciplineColor } from '../config/styleConfig';

interface RaceData {
  race_code: string;
  race_description: string;
  race_date: string;
  race_location: string;
  race_discipline: string;
  race_season?: string;
  race_league?: string;
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

const isNewRace = (raceDate: string): boolean => {
  const race = new Date(raceDate);
  const fifteenDaysAgo = new Date();
  fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
  return race >= fifteenDaysAgo;
};

export default function Races() {
  const navigate = useNavigate();
  const [races, setRaces] = useState<RaceData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Store available seasons and currently selected season for season filter
  const [seasons, setSeasons] = useState<string[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  // Store leagues within the current season and selected league filter
  const [leagues, setLeagues] = useState<string[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<string>('all');

  useEffect(() => {
    const fetchRaces = async () => {
      const { data, error } = await supabase
        .from('medvedica_race_data')
        .select('*')
        .order('race_date', { ascending: false });

      if (error) {
        console.error('Error fetching races:', error);
        return;
      }

      const raceList = (data || []) as RaceData[];
      setRaces(raceList);

      // Derive unique seasons from fetched races and sort them (newest / highest first)
      const uniqueSeasons = Array.from(
        new Set(
          raceList
            .map((race) => race.race_season)
            .filter((season): season is string => Boolean(season))
        )
      ).sort((a, b) => b.localeCompare(a));

      setSeasons(uniqueSeasons);
      setIsLoading(false);
    };

    fetchRaces();
  }, []);

  // Recompute leagues whenever races or selected season change
  useEffect(() => {
    // Take only races from the currently selected season (or all) to compute leagues
    const baseRaces =
      selectedSeason === 'all'
        ? races
        : races.filter((race) => race.race_season === selectedSeason);

    // Derive unique leagues within that season
    const uniqueLeagues = Array.from(
      new Set(
        baseRaces
          .map((race) => race.race_league)
          .filter((league): league is string => Boolean(league))
      )
    ).sort((a, b) => a.localeCompare(b));

    setLeagues(uniqueLeagues);

    // Reset league filter when season changes so we always start with "all"
    setSelectedLeague('all');
  }, [races, selectedSeason]);

  // Apply season + league filter while preserving date ordering from the original query
  const filteredRaces = races.filter((race) => {
    const matchesSeason =
      selectedSeason === 'all' || race.race_season === selectedSeason;
    const matchesLeague =
      selectedLeague === 'all' || race.race_league === selectedLeague;
    return matchesSeason && matchesLeague;
  });

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="50vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header Image - full-width hero like Racers page (desktop) */}
      <Box mb={8} position="relative">
        <Image
          src="/img/races-logo.webp"
          alt="Races"
          width="100%"
          height="300px"
          objectFit="cover"
          borderRadius="lg"
          fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={6}
          background="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))"
          borderBottomRadius="lg"
        >
          <Heading color="white">Race Events</Heading>
        </Box>
      </Box>

      {/* Season filter buttons - behave like a segmented control to switch between seasons and all races */}
      {seasons.length > 0 && (
        <Box mb={6}>
          <Text mb={2} fontWeight="medium">
            Race season
          </Text>
          <ButtonGroup isAttached size="sm" variant="outline">
            <Button
              onClick={() => setSelectedSeason('all')}
              colorScheme={selectedSeason === 'all' ? 'blue' : undefined}
              variant={selectedSeason === 'all' ? 'solid' : 'outline'}
            >
              All
            </Button>
            {seasons.map((season) => (
              <Button
                key={season}
                onClick={() => setSelectedSeason(season)}
                colorScheme={selectedSeason === season ? 'blue' : undefined}
                variant={selectedSeason === season ? 'solid' : 'outline'}
              >
                {season}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}

      {/* Nested league filter - depends on the selected season; when "All" is picked, shows leagues across all seasons */}
      {leagues.length > 0 && (
        <Box mb={6}>
          <Text mb={2} fontWeight="medium">
            Race league
          </Text>
          {/* Use Wrap so league buttons wrap on small screens instead of overflowing horizontally */}
          <Wrap spacing={2}>
            <WrapItem>
              <Button
                size="sm"
                onClick={() => setSelectedLeague('all')}
                colorScheme={selectedLeague === 'all' ? 'blue' : undefined}
                variant={selectedLeague === 'all' ? 'solid' : 'outline'}
              >
                All
              </Button>
            </WrapItem>
            {leagues.map((league) => (
              <WrapItem key={league}>
                <Button
                  size="sm"
                  onClick={() => setSelectedLeague(league)}
                  colorScheme={selectedLeague === league ? 'blue' : undefined}
                  variant={selectedLeague === league ? 'solid' : 'outline'}
                >
                  {league}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {/* Race Cards Grid */}
      <SimpleGrid columns={[1, 2, 3]} spacing={6}>
        {filteredRaces.map((race) => (
          <Box
            key={race.race_code}
            p={5}
            shadow="md"
            borderWidth="1px"
            borderRadius="md"
            cursor="pointer"
            onClick={() => navigate(`/race/${encodeURIComponent(race.race_code)}`)}
            _hover={{ 
              transform: 'translateY(-2px)',
              shadow: 'lg',
              borderColor: 'blue.200'
            }}
            transition="all 0.2s"
          >
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Heading size="md">{race.race_description}
              &nbsp;{isNewRace(race.race_date) && (<Tag size="sm" colorScheme="green" variant="subtle" fontSize="xs">New</Tag>)
              }
              </Heading>
              
            </Box>
            <Text mb={2}>
              <strong>Date:</strong> {formatDate(race.race_date)}
            </Text>
            <Text mb={2}>
              <strong>Location:</strong> {race.race_location}
            </Text> 
            <Text mb={2}>
              <strong>Discipline:</strong> {race.race_discipline && (
                <Tag
                  size="sm"
                  bg={getDisciplineColor(race.race_discipline)}
                  color="white"
                  variant="solid"
                  alignContent='center'
                >
                  {race.race_discipline}
                </Tag>
              )}     
            </Text>  
            
          </Box>
        ))}
      </SimpleGrid>
    </Container>
  );
} 